import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import SocialIcons from '../components/SocialIcons';
import { apiGet, apiPost } from '../api';
import AdminPanel from './AdminPanel';

function App() {
  const [isAdminView, setIsAdminView] = useState(() => {
    try {
      return window.location.hash === '#admin';
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    function onHashChange() {
      setIsAdminView(window.location.hash === '#admin');
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (isAdminView) {
    return <AdminPanel />;
  }

  const [content, setContent] = useState(null);
  const [contentError, setContentError] = useState('');

  const [carePrograms, setCarePrograms] = useState([]);
  const [careProgramsError, setCareProgramsError] = useState('');

  const [doctorsTimeline, setDoctorsTimeline] = useState(null);
  const [doctorsTimelineError, setDoctorsTimelineError] = useState('');

  const [clinicLocation, setClinicLocation] = useState(null);
  const [clinicLocationError, setClinicLocationError] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [formStatus, setFormStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [apptName, setApptName] = useState('');
  const [apptPhone, setApptPhone] = useState('');
  const [apptDate, setApptDate] = useState('');
  const [apptTime, setApptTime] = useState('');
  const [apptReason, setApptReason] = useState('');
  const [apptStatus, setApptStatus] = useState('');
  const [isApptSubmitting, setIsApptSubmitting] = useState(false);

  const apptDateRef = useRef(null);
  const apptTimeRef = useRef(null);

  const openNativePicker = useCallback((ref) => {
    const el = ref && ref.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch (_) {
      try {
        el.focus();
      } catch (__) {
        // ignore
      }
    }
  }, []);

  const [fbName, setFbName] = useState('');
  const [fbRating, setFbRating] = useState(5);
  const [fbMessage, setFbMessage] = useState('');
  const [fbStatus, setFbStatus] = useState('');
  const [isFbSubmitting, setIsFbSubmitting] = useState(false);

  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackError, setFeedbackError] = useState('');

  const [isDataLoading, setIsDataLoading] = useState(true);
  const [storyIndex, setStoryIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsDataLoading(true);

    apiGet('/home-data')
      .then((data) => {
        if (cancelled) return;
        const res = data || {};
        setContent(res.content || null);
        setClinicLocation(res.clinicLocation || null);
        setDoctorsTimeline(res.doctorsTimeline || null);
        setCarePrograms(Array.isArray(res.carePrograms) ? res.carePrograms : []);
        setFeedbackItems(Array.isArray(res.feedbackItems) ? res.feedbackItems : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load initial data', err);
        setContentError('Failed to load content');
        setClinicLocationError('Failed to load clinic location');
        setDoctorsTimelineError('Failed to load timeline');
        setCareProgramsError('Failed to load programs');
        setFeedbackError('Failed to load feedback');
      })
      .finally(() => {
        if (!cancelled) {
          setIsDataLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const carouselReviews = useMemo(() => {
    const items = Array.isArray(feedbackItems) ? feedbackItems : [];
    return items.filter(Boolean).slice(0, 6);
  }, [feedbackItems]);

  const testimonialStories = useMemo(() => [
    {
      id: 'story-1',
      name: 'R.K.',
      meta: '58 years • Paralysis Recovery',
      achievement: 'Walking Without Support',
      duration: 'Treatment Duration: 8 months',
      quote: "After my stroke, I couldn't move my left side. Within 8 months of treatment at Arya Homoeo Hall, I can now walk without support. The care and patience shown by the doctors gave me hope when I had none."
    },
    {
      id: 'story-2',
      name: 'S. Sharma',
      meta: '42 years • Polio Recovery',
      achievement: 'Improved Mobility',
      duration: 'Treatment Duration: 10 months',
      quote: 'I had lost confidence after years of limited movement. The regular treatment and personal guidance at Arya Homoeo Hall helped me regain strength and independence.'
    },
    {
      id: 'story-3',
      name: 'M. Singh',
      meta: '36 years • Nerve Weakness',
      achievement: 'Daily Movement Restored',
      duration: 'Treatment Duration: 6 months',
      quote: 'The doctors listened carefully and designed a treatment plan that truly worked with my body. I now move freely and feel stronger every week.'
    },
    {
      id: 'story-4',
      name: 'A. Verma',
      meta: '61 years • Stroke Recovery',
      achievement: 'Better Balance & Confidence',
      duration: 'Treatment Duration: 9 months',
      quote: 'I was discouraged after my stroke, but treatment here brought structure, care, and visible progress. My balance has improved and I feel hopeful again.'
    },
    {
      id: 'story-5',
      name: 'P. Gupta',
      meta: '49 years • Muscle Weakness',
      achievement: 'Daily Activities Renewed',
      duration: 'Treatment Duration: 7 months',
      quote: 'The attention and discipline in care made all the difference. I can do my daily work with much more ease and confidence than before.'
    }
  ], []);

  const currentStory = testimonialStories[storyIndex % testimonialStories.length] || testimonialStories[0];

  const handleStoryChange = useCallback((direction) => {
    setStoryIndex((prev) => {
      const total = testimonialStories.length || 1;
      return (prev + direction + total) % total;
    });
  }, [testimonialStories.length]);

  const clinicProfile = content && content.clinicProfile ? content.clinicProfile : {};
  const clinic = content && content.clinic ? content.clinic : {};

  const home = content && content.home ? content.home : {};
  const hero = (home && home.hero) ? home.hero : (content && content.hero ? content.hero : {});
  const heroTag = home && home.heroTag ? home.heroTag : {};
  const heroStats = Array.isArray(home && home.heroStats ? home.heroStats : null) ? home.heroStats : [];
  const legacy = home && home.legacy ? home.legacy : {};
  const experience = home && home.experience ? home.experience : {};

  const clinicPhones = Array.isArray(clinicProfile.phones)
    ? clinicProfile.phones
    : Array.isArray(clinic.phones)
      ? clinic.phones
      : [];

  const clinicEmails = Array.isArray(clinicProfile.emails)
    ? clinicProfile.emails
    : Array.isArray(clinic.email)
      ? clinic.email
      : [];

  const clinicHoursText = clinicProfile.hoursText || (Array.isArray(clinic.hours) ? clinic.hours.join('\n') : '');
  const clinicAddressText = clinicProfile.addressText || (Array.isArray(clinic.addressLines) ? clinic.addressLines.join('\n') : '');

  const formatPhone = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (raw.startsWith('+')) return raw;
    const digits = raw.replace(/\D+/g, '');
    if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
    return raw;
  };

  const clinicLogoSrc = useMemo(() => {
    const raw = typeof clinicProfile.logoUrl === 'string' ? clinicProfile.logoUrl : '';
    const url = String(raw || '').trim();
    if (!url) return '/aryalogo-transparent.png';
    const v = String(clinicProfile.updatedAt || '').trim();
    return v ? `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(v)}` : url;
  }, [clinicProfile.logoUrl, clinicProfile.updatedAt]);

  const heroImageSrc = useMemo(() => {
    const raw = home && typeof home.heroImageUrl === 'string' ? home.heroImageUrl : '';
    const url = String(raw || '').trim();
    if (!url) return '/assets/images/main.png';
    const v = String((home && home.updatedAt) || (content && content.updatedAt) || '').trim();
    return v ? `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(v)}` : url;
  }, [content, home]);

  const timelineView = useMemo(() => {
    const tl = doctorsTimeline && typeof doctorsTimeline === 'object' ? doctorsTimeline : {};
    const founders = Array.isArray(tl.founders) ? tl.founders.map((s) => String(s || '').trim()).filter(Boolean) : [];
    const itemsRaw = Array.isArray(tl.timelineItems) ? tl.timelineItems : [];
    const items = itemsRaw
      .filter((it) => it && typeof it === 'object')
      .filter((it) => String(it.status || 'active') === 'active')
      .sort((a, b) => Number(a && a.position ? a.position : 0) - Number(b && b.position ? b.position : 0))
      .map((it) => ({
        id: String(it.id || ''),
        name: String(it.doctorName || ''),
        subtitle: String(it.qualification || ''),
      }))
      .filter((it) => it.name);

    return {
      title: String(tl.title || '').trim(),
      tagline: String(tl.tagline || '').trim(),
      yearLabel: String(tl.yearLabel || '').trim(),
      yearOfEstablishment: String(tl.yearOfEstablishment || '').trim(),
      foundersLabel: String(tl.foundersLabel || '').trim(),
      founders,
      items,
    };
  }, [doctorsTimeline]);

  const activeLocation = clinicLocation && typeof clinicLocation === 'object' ? clinicLocation : null;
  const activeEmbedUrl = activeLocation && typeof activeLocation.embedMapUrl === 'string' ? String(activeLocation.embedMapUrl).trim() : '';
  const activeMapLink = activeLocation && typeof activeLocation.googleMapLink === 'string' ? String(activeLocation.googleMapLink).trim() : '';
  const activeAddressText = activeLocation && typeof activeLocation.addressText === 'string' ? String(activeLocation.addressText).trim() : '';

  const visitAddressText = String(activeAddressText || clinicAddressText || '')
    .split(',')
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join('\n');

  const primaryPhone = clinicProfile.primaryPhone || clinicPhones[0] || '9973062638';
  const whatsappNumberRaw = clinicProfile.whatsappNumber || primaryPhone;

  const displayPhones = useMemo(() => {
    const fromProfile = [clinicProfile.primaryPhone, clinicProfile.secondaryPhone]
      .concat(Array.isArray(clinicProfile.phones) ? clinicProfile.phones : []);
    const fromClinic = Array.isArray(clinic.phones) ? clinic.phones : [];
    const all = fromProfile.concat(fromClinic).map((p) => String(p || '').trim()).filter(Boolean);
    const uniq = [];
    const seen = new Set();
    for (const p of all) {
      const key = p.replace(/\s+/g, '');
      if (seen.has(key)) continue;
      seen.add(key);
      uniq.push(formatPhone(p));
    }
    return uniq.filter(Boolean);
  }, [clinic.phones, clinicProfile.primaryPhone, clinicProfile.secondaryPhone, clinicProfile.phones]);

  const CLINIC_PHONE = primaryPhone;
  const WHATSAPP_NUMBER = String(whatsappNumberRaw || '').replace(/^\+/, '').replace(/\s+/g, '');

  const programsTitle = content && content.programs && typeof content.programs.title === 'string' ? content.programs.title : '';
  const programsSubtitle = content && content.programs && typeof content.programs.subtitle === 'string' ? content.programs.subtitle : '';

  const programItems = useMemo(() => {
    const list = Array.isArray(carePrograms) ? carePrograms : [];
    const cleaned = list.filter((p) => p && p.status === 'active');
    const sortOrder = (it) => {
      const n = Number(it && typeof it.displayOrder !== 'undefined' ? it.displayOrder : 0);
      return Number.isFinite(n) ? n : 0;
    };
    return [...cleaned].sort((a, b) => sortOrder(a) - sortOrder(b));
  }, [carePrograms]);

  const renderProgramIcon = (iconType) => {
    const t = String(iconType || '').toLowerCase();
    if (t === 'paralysis') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M13.9969 20.9954V5.83203" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M17.4963 15.1635C16.4871 14.8685 15.6007 14.2544 14.9698 13.4133C14.339 12.5721 13.9977 11.5492 13.9971 10.4978C13.9964 11.5492 13.6551 12.5721 13.0243 13.4133C12.3934 14.2544 11.507 14.8685 10.4978 15.1635" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5.83203 24.4946H22.1619" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (t === 'polio') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M25.6612 13.9969H22.7685C22.2587 13.9958 21.7626 14.1617 21.3561 14.4693C20.9496 14.7768 20.6549 15.2091 20.5173 15.6999L17.7762 25.4511" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10.2179 2.54272L7.47678 12.294C7.33969 12.7829 7.04682 13.2137 6.64263 13.521C6.23845 13.8284 5.74503 13.9955 5.23726 13.9969H2.33289" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (t === 'neural' || t === 'nerve') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M16.3298 11.123V8.16493C16.3298 6.92752 16.8213 5.74079 17.6963 4.86581C18.5713 3.99083 19.758 3.49927 20.9954 3.49927H22.7451" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4.66565 10.4977C5.53212 9.84787 6.56243 9.45213 7.64114 9.35487C8.71985 9.2576 9.80434 9.46264 10.7731 9.94701" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5.83203 24.4946H22.1619" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (t === 'skin') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M14 25.6667c6.4437 0 11.6667-5.223 11.6667-11.6667S20.4437 2.33333 14 2.33333 2.33333 7.55633 2.33333 14 7.55633 25.6667 14 25.6667Z" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10.5 14c0 1.933 1.567 3.5 3.5 3.5S17.5 15.933 17.5 14" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (t === 'respiratory') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M14 2.33333v23.33334" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 14c-3.5 0-6.41667 2.33333-8.16667 5.83333" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 14c3.5 0 6.41667 2.33333 8.16667 5.83333" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (t === 'joint') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M10.5 4.66667h7c1.933 0 3.5 1.567 3.5 3.5v11.66666c0 1.933-1.567 3.5-3.5 3.5h-7c-1.933 0-3.5-1.567-3.5-3.5V8.16667c0-1.933 1.567-3.5 3.5-3.5Z" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10.5 14h7" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (t === 'liver') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M6.41667 9.33333c2.33333-3.5 6.41667-5.83333 11.08333-5.83333 3.5 0 6.41667 1.16667 8.16667 3.5" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2.33333 14c0 6.4437 5.223 11.6667 11.66667 11.6667S25.6667 20.4437 25.6667 14" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (t === 'infection') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M14 2.33333v23.33334" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2.33333 14h23.33334" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path d="M14 25.6667c6.4437 0 11.6667-5.223 11.6667-11.6667S20.4437 2.33333 14 2.33333 2.33333 7.55633 2.33333 14 7.55633 25.6667 14 25.6667Z" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 8.16667v11.66666" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.16667 14h11.66666" stroke="white" strokeWidth="2.33283" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const openAppointmentForm = () => {
    const el = document.querySelector('#appointment') || document.querySelector('#appointment-form-title') || document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    window.setTimeout(() => {
      const input = document.getElementById('apptName');
      if (input && typeof input.focus === 'function') input.focus();
    }, 350);
  };

  const onBookNow = openAppointmentForm;
  const onBookConsultation = openAppointmentForm;

  const onLearnMore = () => {
    const el = document.querySelector('#programs') || document.querySelector('#legacy') || document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  async function handleSubmit(e){
    e.preventDefault();
    if(!name.trim() || !email.trim() || !message.trim()){
      setFormStatus('Please fill in Name, Email and Message');
      return;
    }
    setFormStatus('Sending...');
    setIsSubmitting(true);

    try {
      const json = await apiPost('/contact', {
        name,
        email,
        phone,
        message,
      });

      setFormStatus(json.message || 'Message sent — we will respond within 24 hours.');
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
      setTimeout(() => setFormStatus(''), 5000);
    } catch (err) {
      setFormStatus(err && err.message ? err.message : 'Network error');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleAppointmentSubmit(e) {
    e.preventDefault();
    if (!apptName.trim() || !apptPhone.trim()) {
      setApptStatus('Please fill in Name and Phone');
      return;
    }

    setApptStatus('Sending...');
    setIsApptSubmitting(true);

    try {
      const json = await apiPost('/appointments', {
        name: apptName,
        phone: apptPhone,
        date: apptDate,
        time: apptTime,
        reason: apptReason,
      });

      setApptStatus(json.message || 'Appointment request received.');
      setApptName('');
      setApptPhone('');
      setApptDate('');
      setApptTime('');
      setApptReason('');
      setTimeout(() => setApptStatus(''), 5000);
    } catch (err) {
      setApptStatus(err && err.message ? err.message : 'Network error');
    } finally {
      setIsApptSubmitting(false);
    }
  }

  async function handleFeedbackSubmit(e) {
    e.preventDefault();
    if (!fbName.trim() || !fbMessage.trim()) {
      setFbStatus('Please fill in Name and Feedback');
      return;
    }

    const ratingNum = Number(fbRating);
    if (!Number.isFinite(ratingNum) || ratingNum < 1 || ratingNum > 5) {
      setFbStatus('Please select a rating');
      return;
    }

    setFbStatus('Sending...');
    setIsFbSubmitting(true);

    try {
      const json = await apiPost('/feedback', {
        name: fbName,
        rating: ratingNum,
        message: fbMessage,
      });

      setFbStatus(json.message || 'Feedback received. Thank you!');
      if (json && json.data) {
        setFeedbackItems((prev) => [json.data, ...(Array.isArray(prev) ? prev : [])]);
      }
      setFbName('');
      setFbRating(5);
      setFbMessage('');
      setTimeout(() => setFbStatus(''), 5000);
    } catch (err) {
      setFbStatus(err && err.message ? err.message : 'Network error');
    } finally {
      setIsFbSubmitting(false);
    }
  }
  if (isDataLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#f9f9f9', fontFamily: 'inherit' }}>
        <div style={{ width: '50px', height: '50px', border: '4px solid #ddd', borderTop: '4px solid #8B6914', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <p style={{ marginTop: '20px', color: '#555', fontSize: '1.2rem' }}>Loading Arya Homoeo Hall...</p>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="nav">
        <div className="nav-left">
          <img
            src={clinicLogoSrc}
            alt="Arya Homoeo Hall Logo"
            className="brand-logo"
            onError={(e) => {
              try {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/aryalogo-transparent.png';
              } catch (_) {
              }
            }}
          />
          <div className="brand-text">
            <div className="brand-title">{clinicProfile.name || clinic.name || 'Arya Homoeo Hall'}</div>
            <div className="brand-subtitle">Since {clinicProfile.since || clinic.since || '1985'}</div>
          </div>
        </div>

        <nav className="nav-links" aria-label="Primary" onClick={(e)=>{
          const a = e.target.closest('a');
          if(!a) return;
          const hash = a.getAttribute('href');
          if(!hash || !hash.startsWith('#')) return;
          e.preventDefault();
          const el = document.querySelector(hash);
          if (el) el.scrollIntoView({behavior:'smooth', block:'start'});
        }}>
          <a href="#legacy">Legacy</a>
          <a href="#services">Services</a>
          <a href="#contact">Contact</a>
          <a href="#testimonials">Testimonials</a>
        </nav>

        <div className="nav-right">
          <div className="nav-phone">
            <span role="img" aria-label="phone">
              📞
            </span>
            <span>{CLINIC_PHONE}</span>
          </div>
          <button className="btn-primary" onClick={onBookNow}>Book Now</button>
        </div>
      </header>

      <main className="hero">
        <section className="hero-left">
          <div className="pill-badge">
            <div className="pill-icon" role="img" aria-label="medicine">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15.477 12.89L16.992 21.416C17.009 21.5164 16.9949 21.6196 16.9516 21.7118C16.9084 21.8039 16.838 21.8807 16.7499 21.9318C16.6619 21.9829 16.5603 22.0059 16.4588 21.9977C16.3573 21.9895 16.2607 21.9506 16.182 21.886L12.602 19.199C12.4292 19.0699 12.2192 19.0001 12.0035 19.0001C11.7878 19.0001 11.5778 19.0699 11.405 19.199L7.819 21.885C7.74032 21.9494 7.64386 21.9883 7.54249 21.9965C7.44112 22.0047 7.33967 21.9818 7.25166 21.9309C7.16365 21.8799 7.09327 21.8033 7.04991 21.7113C7.00656 21.6193 6.99228 21.5163 7.009 21.416L8.523 12.89" stroke="#8B6914" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 14C15.3137 14 18 11.3137 18 8C18 4.68629 15.3137 2 12 2C8.68629 2 6 4.68629 6 8C6 11.3137 8.68629 14 12 14Z" stroke="#8B6914" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>{clinicProfile.tagline || 'Healing Since 1985'}</span>
          </div>

          <h1 className="hero-title">
            {(Array.isArray(hero.titleLines) && hero.titleLines.length
              ? hero.titleLines
              : ['Restoring', 'Movement,', 'Restoring Life']
            ).map((line) => (
              <span key={line}>{line}</span>
            ))}
          </h1>

          <p className="hero-body">
            {hero.subtitle || (
              'For over a century, Arya Homoeo Hall has been a beacon of hope for those seeking natural, holistic healing. '
              + 'Specializing in paralysis and polio care, we combine time-tested homeopathic wisdom with compassionate '
              + 'treatment.'
            )}
          </p>

          <div className="hero-actions">
            <button className="btn-primary" onClick={onBookConsultation}>{hero.ctaPrimary || 'Book Consultation'}</button>
            <button className="btn-ghost" onClick={onLearnMore}>{hero.ctaSecondary || 'Learn More'}</button>
          </div>

          <div className="hero-stats">
            {(heroStats && heroStats.length ? heroStats : [
              { value: '41+', label: 'Years of Trust' },
              { value: '10K+', label: 'Lives Touched' },
              { value: '100%', label: 'Natural Care' },
            ]).map((s, idx) => (
              <div className="stat" key={`${s.value}-${s.label}-${idx}`}>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        <aside className="hero-right" aria-label="Clinic highlight">
          <div className="hero-card">
            <img
              src={heroImageSrc}
              alt={heroTag.subtitle || 'Clinic highlight'}
              className="hero-card-img"
              onError={(e) => {
                try {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/assets/images/main.png';
                } catch (_) {
                }
              }}
            />
          </div>
          <div className="hero-tag">
            <div className="hero-tag-title">{heroTag.title || 'Est. 1985'}</div>
            <div className="hero-tag-subtitle">{heroTag.subtitle || '41 Years of Care'}</div>
          </div>
        </aside>
      </main>

      <section className="timeline-section" id="legacy">
        <div className="timeline-header">
          {timelineView.title ? <h2 className="timeline-title">{timelineView.title}</h2> : null}
          {timelineView.tagline ? <p className="timeline-subtitle">{timelineView.tagline}</p> : null}
        </div>

        {timelineView.items.length ? (
          <>
            <div className="legacy-summary">
              <div className="legacy-summary-grid">
                {timelineView.yearOfEstablishment ? (
                  <article className="legacy-summary-card">
                    <div className="legacy-summary-icon" aria-hidden="true">
                      <span className="legacy-summary-icon-inner">📅</span>
                    </div>
                    <div className="legacy-summary-content">
                      <h3 className="legacy-summary-title">{timelineView.yearLabel || 'Year of Establishment'}</h3>
                      <p className="legacy-summary-text">{timelineView.yearOfEstablishment}</p>
                    </div>
                  </article>
                ) : null}

                {timelineView.founders.length ? (
                  <article className="legacy-summary-card">
                    <div className="legacy-summary-icon" aria-hidden="true">
                      <span className="legacy-summary-icon-inner">🏛️</span>
                    </div>
                    <div className="legacy-summary-content">
                      <h3 className="legacy-summary-title">{timelineView.foundersLabel || 'Founder'}</h3>
                      <ul className="legacy-summary-list">
                        {timelineView.founders.map((name, idx) => (
                          <li key={`${name}-${idx}`}>{name}</li>
                        ))}
                      </ul>
                    </div>
                  </article>
                ) : null}
              </div>
            </div>

            <div className="timeline">
              {timelineView.items.map((doc, idx) => {
            const left = idx % 2 === 0;
            return (
              <div className="timeline-step" key={`${doc.id || doc.name}-${idx}`}>
                {left ? (
                  <div className="timeline-card timeline-card-left">
                    <h3 className="timeline-card-title">{doc.name}</h3>
                    <p className="timeline-card-body">{doc.subtitle || '—'}</p>
                  </div>
                ) : (
                  <div className="timeline-spacer" />
                )}

                <div className="timeline-center" aria-hidden="true">
                  <div className="timeline-node">
                    <span />
                  </div>
                </div>

                {left ? (
                  <div className="timeline-spacer" />
                ) : (
                  <div className="timeline-card timeline-card-right">
                    <h3 className="timeline-card-title">{doc.name}</h3>
                    <p className="timeline-card-body">{doc.subtitle || '—'}</p>
                  </div>
                )}
              </div>
            );
              })}
            </div>
          </>
        ) : (
          <div className="timeline-subtitle" style={{ textAlign: 'center', marginTop: 16 }}>
            {doctorsTimelineError ? 'Timeline will be updated soon.' : 'Timeline will be updated soon.'}
          </div>
        )}
      </section>
      <section className="experience-section" aria-labelledby="experience-heading">
        <div className="experience-inner">
          <h2 className="experience-title" id="experience-heading">
            {experience.title || '41 Years of Experience'}
          </h2>
          <p className="experience-quote">
            {experience.quote
              ? `"${experience.quote}"`
              : (
                '"What began as a single practitioner\'s vision has grown into a legacy of healing.\n'
                + "We've witnessed medical evolution while staying true to the core principles of\n"
                + 'homeopathy— treating the whole person, not just symptoms."'
              )}
          </p>
          <div className="experience-divider" />
          <p className="experience-author">{experience.author || '— The Arya Family Legacy'}</p>
        </div>
      </section>
      <section className="programs-section" id="services">
        <div className="programs-inner">
          {programsTitle ? <h2 className="programs-title">{programsTitle}</h2> : null}
          {programsSubtitle ? <p className="programs-subtitle">{programsSubtitle}</p> : null}

          <div className="programs-grid">
            {programItems.map((p) => {
              const iconKey = String(p && p.iconType ? p.iconType : '');
              const displayTitle = String(p && p.title ? p.title : '');
              const displayDescription = String(p && p.shortDescription ? p.shortDescription : '');
              const displayBullets = Array.isArray(p && p.bulletPoints ? p.bulletPoints : null) ? p.bulletPoints : [];
              const id = String(p && p.id ? p.id : '');

              return (
                <article className={`program-card program-card-${iconKey}`} key={id || displayTitle}>
                  <div className={`program-icon program-icon-primary program-icon-${iconKey}`} aria-hidden="true">
                    {renderProgramIcon(iconKey)}
                  </div>
                  <h3 className="program-card-title">{displayTitle}</h3>
                  {displayDescription ? <p className="program-card-body">{displayDescription}</p> : null}
                  {displayBullets.length > 0 ? (
                    <ul className="program-list">
                      {displayBullets.map((b, idx) => (
                        <li key={`${id}-${idx}`}>{String(b || '')}</li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              );
            })}
          </div>

          {!programItems.length && !careProgramsError ? (
            <div className="programs-subtitle" style={{ textAlign: 'center', marginTop: 14 }}>Programs will be updated soon.</div>
          ) : null}

          {careProgramsError ? (
            <div className="programs-subtitle" style={{ textAlign: 'center', marginTop: 14 }}>Programs will be updated soon.</div>
          ) : null}
        </div>
      </section>
      <section className="why-section" aria-labelledby="why-heading">
        <h2 className="why-title" id="why-heading">
          Why Choose Arya Homoeo Hall?
        </h2>
        <div className="why-features">
          <article className="why-feature">
            <div className="why-icon why-icon-patient" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M2.66666 12.6666C2.66668 11.1829 3.11678 9.73408 3.9575 8.51152C4.79822 7.28896 5.99001 6.35018 7.37547 5.81917C8.76092 5.28816 10.2749 5.1899 11.7173 5.53736C13.1598 5.88483 14.463 6.66168 15.4547 7.76531C15.5245 7.83999 15.609 7.89953 15.7028 7.94024C15.7966 7.98095 15.8977 8.00195 16 8.00195C16.1022 8.00195 16.2034 7.98095 16.2972 7.94024C16.391 7.89953 16.4755 7.83999 16.5453 7.76531C17.5339 6.65451 18.8374 5.87113 20.2822 5.51944C21.727 5.16775 23.2446 5.26443 24.6332 5.79662C26.0217 6.3288 27.2152 7.27124 28.0548 8.49851C28.8945 9.72577 29.3404 11.1797 29.3333 12.6666C29.3333 15.72 27.3333 18.0000 25.3333 20L18.0107 27.084C17.7622 27.3693 17.4559 27.5985 17.1121 27.7564C16.7682 27.9142 16.3947 27.9971 16.0164 27.9995C15.6380 28.0019 15.2635 27.9238 14.9177 27.7703C14.5719 27.6168 14.2627 27.3915 14.0107 27.1093L6.66666 20C4.66666 18 2.66666 15.7333 2.66666 12.6666Z" stroke="black" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="why-feature-title">Patient-Centered Care</h3>
            <p className="why-feature-body">
              Individual treatment plans tailored to your unique needs
            </p>
          </article>
          <article className="why-feature">
            <div className="why-icon why-icon-natural" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M14.6667 26.6667C12.3254 26.6738 10.067 25.8007 8.33937 24.2206C6.61172 22.6405 5.54103 20.4688 5.33966 18.1362C5.13829 15.8036 5.82093 13.4806 7.25221 11.6277C8.68349 9.77491 10.7589 8.52768 13.0667 8.13341C20.6667 6.66675 22.6667 5.97341 25.3333 2.66675C26.6667 5.33341 28 8.24008 28 13.3334C28 20.6667 21.6267 26.6667 14.6667 26.6667Z" stroke="black" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2.66669 28C2.66669 24 5.13335 20.8533 9.44002 20C12.6667 19.36 16 17.3333 17.3334 16" stroke="black" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="why-feature-title">100% Natural</h3>
            <p className="why-feature-body">
              No side effects, purely homeopathic remedies
            </p>
          </article>
          <article className="why-feature">
            <div className="why-icon why-icon-proven" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M5.33336 18.6666C5.08105 18.6674 4.83367 18.5967 4.61997 18.4625C4.40626 18.3284 4.23501 18.1364 4.1261 17.9088C4.01718 17.6812 3.97509 17.4273 4.0047 17.1768C4.0343 16.9262 4.1344 16.6892 4.29336 16.4932L17.4934 2.89323C17.5924 2.77893 17.7273 2.7017 17.876 2.6742C18.0247 2.6467 18.1783 2.67058 18.3117 2.7419C18.445 2.81322 18.5501 2.92775 18.6098 3.0667C18.6695 3.20565 18.6801 3.36076 18.64 3.50656L16.08 11.5332C16.0045 11.7353 15.9792 11.9526 16.0062 12.1666C16.0331 12.3806 16.1116 12.5848 16.2348 12.7618C16.3581 12.9388 16.5224 13.0832 16.7137 13.1828C16.9051 13.2823 17.1177 13.3339 17.3334 13.3332H26.6667C26.919 13.3324 27.1664 13.4031 27.3801 13.5373C27.5938 13.6714 27.7651 13.8634 27.874 14.091C27.9829 14.3186 28.025 14.5724 27.9954 14.823C27.9658 15.0736 27.8657 15.3106 27.7067 15.5066L14.5067 29.1066C14.4077 29.2208 14.2728 29.2981 14.1241 29.3256C13.9754 29.3531 13.8217 29.3292 13.6884 29.2579C13.555 29.1866 13.4499 29.072 13.3903 28.9331C13.3306 28.7941 13.3199 28.639 13.36 28.4932L15.92 20.4666C15.9955 20.2645 16.0209 20.0472 15.9939 19.8332C15.9669 19.6192 15.8885 19.415 15.7652 19.238C15.642 19.061 15.4777 18.9165 15.2863 18.817C15.095 18.7175 14.8824 18.6659 14.6667 18.6666H5.33336Z" stroke="black" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="why-feature-title">Proven Results</h3>
            <p className="why-feature-body">
              Over 40 years of successful patient outcomes
            </p>
          </article>
        </div>
      </section>
      <section className="how-section" aria-labelledby="how-heading">
        <div className="how-inner">
          <h2 className="how-title" id="how-heading">
            How Homeopathy<span> Restores Movement</span>
          </h2>
          <div className="how-grid">
            <article className="how-item">
              <div className="how-badge">1</div>
              <div className="how-item-text">
                <h3 className="how-item-title">Nerve Stimulation</h3>
                <p className="how-item-body">
                  Natural remedies work at the cellular level to reactivate dormant neural pathways
                </p>
              </div>
            </article>
            <article className="how-item">
              <div className="how-badge">2</div>
              <div className="how-item-text">
                <h3 className="how-item-title">Muscle Recovery</h3>
                <p className="how-item-body">
                  Targeted treatments prevent atrophy and promote gradual strength building
                </p>
              </div>
            </article>
            <article className="how-item">
              <div className="how-badge">3</div>
              <div className="how-item-text">
                <h3 className="how-item-title">Holistic Healing</h3>
                <p className="how-item-body">
                  Address root causes, not just symptoms, for sustainable recovery
                </p>
              </div>
            </article>
            <article className="how-item">
              <div className="how-badge">4</div>
              <div className="how-item-text">
                <h3 className="how-item-title">Zero Side Effects</h3>
                <p className="how-item-body">
                  Safe for all ages with no harmful chemical interactions
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section
        className="testimonials-section"
        id="testimonials"
        aria-labelledby="stories-heading"
      >
        <div className="testimonials-header">
          <h2 className="testimonials-title" id="stories-heading">
            Stories of<span> Hope &amp; Healing</span>
          </h2>
          <p className="testimonials-subtitle">
            Real patients, real recovery. These testimonials represent the thousands of lives transformed through our natural
            healing approach.
          </p>
        </div>

        <div className="testimonial-card">
          <div className="testimonial-bubble">
            <div className="testimonial-bubble-inner">
              <div className="testimonial-quote-marks">“</div>
              <p className="testimonial-text">{currentStory.quote}</p>
              <div className="testimonial-person-name">{currentStory.name}</div>
              <div className="testimonial-person-meta">{currentStory.meta}</div>
              <div className="testimonial-extra-line">
                <span className="testimonial-extra-dot" aria-hidden="true" />
                <span className="testimonial-extra-text">{currentStory.achievement}</span>
              </div>
              <div className="testimonial-duration">{currentStory.duration}</div>
            </div>
          </div>

          <div className="testimonial-nav">
            <div className="testimonial-nav-buttons">
              <button type="button" className="testimonial-nav-btn" aria-label="Previous story" onClick={() => handleStoryChange(-1)}>
                ‹
              </button>
              <button type="button" className="testimonial-nav-btn" aria-label="Next story" onClick={() => handleStoryChange(1)}>
                ›
              </button>
            </div>
            <div className="testimonial-nav-dots" aria-hidden="true">
              {testimonialStories.map((story, index) => (
                <span
                  key={story.id}
                  className={`testimonial-dot ${index === storyIndex ? 'testimonial-dot--active' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="stats-strip-section" aria-label="Clinic statistics">
        <div className="stats-strip-grid">
          <div className="stats-strip-card">
            <div className="stats-strip-value">94%</div>
            <div className="stats-strip-label">Patient Satisfaction</div>
          </div>
          <div className="stats-strip-card">
            <div className="stats-strip-value">87%</div>
            <div className="stats-strip-label">Show Improvement</div>
          </div>
          <div className="stats-strip-card">
            <div className="stats-strip-value">4.8★</div>
            <div className="stats-strip-label">Average Rating</div>
          </div>
          <div className="stats-strip-card">
            <div className="stats-strip-value">10K+</div>
            <div className="stats-strip-label">Patients Treated</div>
          </div>
        </div>
      </section>
      <section className="contact-section" id="contact">
        <div className="contact-header">
          <h2 className="contact-title">Get in Touch</h2>
          <p className="contact-subtitle">
            Ready to start your healing journey? Reach out to us for consultations, appointments, or any questions about
            our treatments.
          </p>
        </div>
        <div className="contact-grid">
          <div className="contact-cards-panel">
            <div className="contact-cards">
              <article className="contact-card">
                <div className="contact-card-icon" aria-hidden="true">
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ width: 24, height: 24, flexShrink: 0 }}
                    >
                      <path
                        d="M20 10C20 14.993 14.461 20.193 12.601 21.799C12.4277 21.9293 12.2168 21.9998 12 21.9998C11.7832 21.9998 11.5723 21.9293 11.399 21.799C9.539 20.193 4 14.993 4 10C4 7.87827 4.84285 5.84344 6.34315 4.34315C7.84344 2.84285 9.87827 2 12 2C14.1217 2 16.1566 2.84285 17.6569 4.34315C19.1571 5.84344 20 7.87827 20 10Z"
                        stroke="#0F2410"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                        stroke="#0F2410"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                <div className="contact-card-text">
                  <h3 className="contact-card-title">Visit Us</h3>
                  <p className="contact-card-body contact-address-multiline">{visitAddressText}</p>
                </div>
              </article>

              <div className="contact-map-card" aria-label="Google Map">
                {activeEmbedUrl ? (
                  <>
                    <a
                      className="contact-map-link"
                      href={activeMapLink || undefined}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Open location in Google Maps"
                    />
                    <iframe
                      className="contact-map-iframe"
                      title="Clinic location on Google Maps"
                      src={activeEmbedUrl}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </>
                ) : (
                  <div className="contact-map-empty">
                    <div className="contact-map-empty-title">Location will be updated soon</div>
                    {clinicLocationError ? <div className="contact-map-empty-sub">{clinicLocationError}</div> : null}
                    {activeMapLink ? (
                      <a className="contact-map-empty-btn" href={activeMapLink} target="_blank" rel="noreferrer">
                        Get Directions
                      </a>
                    ) : null}
                  </div>
                )}
              </div>

              {activeMapLink ? (
                <div style={{ marginTop: 12 }}>
                  <a className="btn-ghost" href={activeMapLink} target="_blank" rel="noreferrer">
                    Get Directions
                  </a>
                </div>
              ) : null}

              <article className="contact-card">
                <div className="contact-card-icon" aria-hidden="true">
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ width: 24, height: 24, flexShrink: 0 }}
                    >
                      <path
                        d="M13.832 16.568C14.0385 16.6628 14.2712 16.6845 14.4917 16.6294C14.7122 16.5744 14.9073 16.4458 15.045 16.265L15.4 15.8C15.5863 15.5516 15.8279 15.35 16.1056 15.2111C16.3833 15.0723 16.6895 15 17 15H20C20.5304 15 21.0391 15.2107 21.4142 15.5858C21.7893 15.9609 22 16.4696 22 17V20C22 20.5304 21.7893 21.0391 21.4142 21.4142C21.0391 21.7893 20.5304 22 20 22C15.2261 22 10.6477 20.1036 7.27208 16.7279C3.89642 13.3523 2 8.7739 2 4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H7C7.53043 2 8.03914 2.21071 8.41421 2.58579C8.78929 2.96086 9 3.46957 9 4V7C9 7.31049 8.92771 7.61672 8.78885 7.89443C8.65 8.17214 8.44839 8.41371 8.2 8.6L7.732 8.951C7.54842 9.09118 7.41902 9.29059 7.36579 9.51535C7.31256 9.74012 7.33878 9.97638 7.44 10.184C8.80668 12.9599 11.0544 15.2048 13.832 16.568Z"
                        stroke="#0F2410"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                <div className="contact-card-text">
                  <h3 className="contact-card-title">Call Us</h3>
                  <p className="contact-card-body">
                    {(displayPhones.length ? displayPhones : [formatPhone(CLINIC_PHONE)]).slice(0, 2).join('\u00A0|\u00A0')}
                  </p>
                </div>
              </article>

              <article className="contact-card">
                <div className="contact-card-icon" aria-hidden="true">
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ width: 24, height: 24, flexShrink: 0 }}
                    >
                      <path
                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                        stroke="#0F2410"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                        stroke="#0F2410"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                <div className="contact-card-text">
                  <h3 className="contact-card-title">Clinic Hours</h3>
                  <p className="contact-card-body contact-hours-singleline">
                    {String(clinicHoursText || '')
                      .replace(/\s*\n+\s*/g, ' | ')
                      .replace(/(\d{1,2}:\d{2})\s*(AM|PM)\b/g, '$1\u00A0$2')}
                  </p>
                </div>
              </article>

              <article className="contact-card">
                <div className="contact-card-icon" aria-hidden="true">
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ width: 24, height: 24, flexShrink: 0 }}
                    >
                      <path
                        d="M22 7L13.009 12.727C12.7039 12.9042 12.3573 12.9976 12.0045 12.9976C11.6517 12.9976 11.3051 12.9042 11 12.727L2 7"
                        stroke="#0F2410"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 4H4C2.89543 4 2 4.89543 2 6V18C2 19.1046 2.89543 20 4 20H20C21.1046 20 22 19.1046 22 18V6C22 4.89543 21.1046 4 20 4Z"
                        stroke="#0F2410"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                <div className="contact-card-text">
                  <h3 className="contact-card-title">Email Us</h3>
                  <p className="contact-card-body">
                    {(clinicEmails.length ? clinicEmails : []).map((em, idx) => (
                      <span key={em}>
                        {em}
                        {idx < clinicEmails.length - 1 ? <br /> : null}
                      </span>
                    ))}
                  </p>
                </div>
              </article>

              <div className="feedback-card" aria-labelledby="feedback-title">
                <h3 className="feedback-title" id="feedback-title">
                  Patient Feedback
                </h3>
                <p className="feedback-subtitle">Share your experience so we can serve you better.</p>
                <form className="feedback-form" onSubmit={handleFeedbackSubmit}>
                  <div className="feedback-grid">
                    <div className="feedback-field">
                      <label className="feedback-label" htmlFor="fbName">
                        Your Name
                      </label>
                      <input
                        id="fbName"
                        className="feedback-input"
                        value={fbName}
                        onChange={(e) => setFbName(e.target.value)}
                        type="text"
                        placeholder="Enter your name"
                      />
                    </div>

                    <div className="feedback-field">
                      <label className="feedback-label" htmlFor="fbRating">
                        Rating
                      </label>
                      <select
                        id="fbRating"
                        className="feedback-input"
                        value={String(fbRating)}
                        onChange={(e) => setFbRating(Number(e.target.value))}
                      >
                        <option value="5">★★★★★ (5)</option>
                        <option value="4">★★★★☆ (4)</option>
                        <option value="3">★★★☆☆ (3)</option>
                        <option value="2">★★☆☆☆ (2)</option>
                        <option value="1">★☆☆☆☆ (1)</option>
                      </select>
                    </div>
                  </div>

                  <div className="feedback-field">
                    <label className="feedback-label" htmlFor="fbMessage">
                      Your Feedback
                    </label>
                    <textarea
                      id="fbMessage"
                      className="feedback-textarea"
                      value={fbMessage}
                      onChange={(e) => setFbMessage(e.target.value)}
                      placeholder="Write your review..."
                    />
                  </div>

                  <button type="submit" className="feedback-submit" disabled={isFbSubmitting}>
                    {isFbSubmitting ? 'Sending...' : 'Submit Feedback'}
                  </button>
                  {fbStatus ? <div className="feedback-status">{fbStatus}</div> : null}
                </form>
              </div>
            </div>
          </div>

          <div className="contact-form-stack" aria-label="Contact forms">
            <div className="contact-form-panel" id="appointment" aria-labelledby="appointment-form-title">
              <h3 className="contact-form-title" id="appointment-form-title">
                Request an Appointment
              </h3>
              <form
                className="contact-form-grid"
                onSubmit={handleAppointmentSubmit}
                aria-labelledby="appointment-form-title"
              >
                <div className="form-group">
                  <label className="form-label" htmlFor="apptName">
                    Your Name
                  </label>
                  <input
                    className="form-control"
                    id="apptName"
                    type="text"
                    placeholder="Enter your name"
                    value={apptName}
                    onChange={e => setApptName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="apptPhone">
                    Phone Number
                  </label>
                  <input
                    className="form-control"
                    id="apptPhone"
                    type="tel"
                    placeholder="+91 XXXXXX XXXXX"
                    value={apptPhone}
                    onChange={e => setApptPhone(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="apptDate">
                    Preferred Date
                  </label>
                  <div className="form-control-wrapper">
                    <button
                      type="button"
                      className="form-control-icon-btn"
                      aria-label="Select date"
                      onClick={() => openNativePicker(apptDateRef)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </button>
                    <input
                      ref={apptDateRef}
                      className="form-control form-control--with-icon"
                      id="apptDate"
                      type="date"
                      value={apptDate}
                      onChange={(e) => setApptDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="apptTime">
                    Preferred Time
                  </label>
                  <div className="form-control-wrapper">
                    <button
                      type="button"
                      className="form-control-icon-btn"
                      aria-label="Select time"
                      onClick={() => openNativePicker(apptTimeRef)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </button>
                    <input
                      ref={apptTimeRef}
                      className="form-control form-control--with-icon"
                      id="apptTime"
                      type="time"
                      value={apptTime}
                      onChange={(e) => setApptTime(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="apptReason">
                    Reason (optional)
                  </label>
                  <textarea
                    className="form-textarea"
                    id="apptReason"
                    placeholder="Short reason / condition"
                    value={apptReason}
                    onChange={e => setApptReason(e.target.value)}
                  />
                </div>
                <button type="submit" className="contact-submit-btn" disabled={isApptSubmitting}>
                  <span>{isApptSubmitting ? 'Sending...' : 'Send Appointment Request'}</span>
                </button>
                {apptStatus && <div className="contact-form-status">{apptStatus}</div>}
                <div className="contact-form-footer">We will confirm your appointment by phone</div>
              </form>
            </div>

            <div className="contact-form-panel" aria-labelledby="contact-form-title">
              <h3 className="contact-form-title" id="contact-form-title">
                Ask the Doctor
              </h3>
              <form className="contact-form-grid" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="name">
                    Your Name
                  </label>
                  <input
                    className="form-control"
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    Email Address
                  </label>
                  <input
                    className="form-control"
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="phone">
                    Phone Number
                  </label>
                  <input
                    className="form-control"
                    id="phone"
                    type="tel"
                    placeholder="+91 XXXXXX XXXXX"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="message">
                    Your Message
                  </label>
                  <textarea
                    className="form-textarea"
                    id="message"
                    placeholder="Tell us about your condition or question..."
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                  />
                </div>
                <button type="submit" className="contact-submit-btn" disabled={isSubmitting}>
                  <span className="contact-icon" aria-hidden="true">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M12.1133 18.0716C12.145 18.1505 12.2 18.2179 12.271 18.2646C12.3421 18.3113 12.4257 18.3352 12.5107 18.333C12.5957 18.3308 12.678 18.3027 12.7465 18.2524C12.815 18.2021 12.8666 18.1321 12.8941 18.0516L18.3108 2.2183C18.3375 2.14446 18.3426 2.06455 18.3255 1.98793C18.3084 1.9113 18.2698 1.84113 18.2143 1.78561C18.1588 1.7301 18.0886 1.69154 18.012 1.67446C17.9354 1.65737 17.8555 1.66246 17.7816 1.68913L1.94831 7.1058C1.86789 7.13338 1.79783 7.1849 1.74754 7.25344C1.69725 7.32199 1.66912 7.40428 1.66695 7.48926C1.66477 7.57425 1.68864 7.65787 1.73536 7.7289C1.78208 7.79993 1.84941 7.85497 1.92831 7.88663L8.53665 10.5366C8.74555 10.6203 8.93536 10.7453 9.09462 10.9043C9.25388 11.0633 9.3793 11.2529 9.46331 11.4616L12.1133 18.0716Z"
                        stroke="white"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.2116 1.78906L9.09497 10.9049"
                        stroke="white"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                </button>
                {formStatus && <div className="contact-form-status">{formStatus}</div>}
                <div className="contact-form-footer">We typically respond within 24 hours</div>
              </form>
            </div>
          </div>
        </div>
      </section>
      <section className="emergency-section" aria-label="Emergency consultations">
        <div className="emergency-panel" aria-label="Customer reviews">
          <div className="emergency-reviews-grid">
            {carouselReviews && carouselReviews.length ? (
              <div className="emergency-marquee" aria-label="Customer reviews">
                <div className="emergency-marquee-track">
                  {[...carouselReviews, ...carouselReviews].map((it, idx) => {
                    const rating = Math.max(0, Math.min(5, Number(it && it.rating) || 0));
                    const filled = '★★★★★'.slice(0, rating);
                    const empty = '☆☆☆☆☆'.slice(0, 5 - rating);
                    const displayName = (it && it.name) ? it.name : 'Anonymous';

                    return (
                      <div
                        className={`emergency-review ${idx % 2 === 1 ? 'is-orange' : 'is-dark'}`}
                        key={`${it.id || `${displayName}-${it.createdAt}`}-${idx}`}
                        aria-label="Customer review"
                      >
                        <div className="emergency-review-quote" aria-hidden="true">“</div>
                        <div className="emergency-review-avatar" aria-hidden="true">
                          <div className="emergency-review-avatar-inner">
                            {String(displayName).trim().slice(0, 1).toUpperCase() || 'A'}
                          </div>
                        </div>
                        <div className="emergency-review-bubble">
                          <div className="emergency-review-stars" aria-label={`Rating ${rating} out of 5`}>{filled}{empty}</div>
                          <div className="emergency-review-message">{(it && it.message) ? it.message : ''}</div>
                          <div className="emergency-review-name">{displayName}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="emergency-review-empty">No customer reviews yet.</div>
            )}

            {(!carouselReviews || !carouselReviews.length) && feedbackError ? (
              <div className="emergency-review-empty">{feedbackError}</div>
            ) : null}
          </div>
        </div>
        <div className="emergency-bar">
          <div className="emergency-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M11.5212 13.7997C11.6932 13.8787 11.887 13.8968 12.0707 13.8509C12.2543 13.805 12.4169 13.698 12.5315 13.5473L12.8272 13.16C12.9824 12.9531 13.1836 12.7852 13.4149 12.6695C13.6462 12.5539 13.9013 12.4937 14.1599 12.4937H16.6587C17.1005 12.4937 17.5243 12.6692 17.8367 12.9816C18.1491 13.294 18.3246 13.7177 18.3246 14.1595V16.6584C18.3246 17.1002 18.1491 17.5239 17.8367 17.8363C17.5243 18.1487 17.1005 18.3242 16.6587 18.3242C12.6824 18.3242 8.86889 16.7446 6.05719 13.9329C3.24549 11.1212 1.66589 7.30774 1.66589 3.3314C1.66589 2.88958 1.8414 2.46586 2.15382 2.15345C2.46623 1.84104 2.88995 1.66553 3.33176 1.66553H5.83057C6.27238 1.66553 6.6961 1.84104 7.00851 2.15345C7.32093 2.46586 7.49644 2.88958 7.49644 3.3314V5.8302C7.49644 6.08882 7.43622 6.34389 7.32057 6.5752C7.20491 6.80652 7.03698 7.00773 6.83009 7.1629L6.44028 7.45526C6.28736 7.57201 6.17958 7.73811 6.13525 7.92532C6.09091 8.11254 6.11275 8.30933 6.19706 8.48226C7.33541 10.7944 9.20764 12.6643 11.5212 13.7997Z"
                stroke="#1F4547"
                strokeWidth="1.66587"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="emergency-text">
            <h3 className="emergency-title">Emergency Consultations</h3>
            <p className="emergency-body">
              For urgent cases or acute paralysis symptoms, please call our emergency line: {CLINIC_PHONE}
            </p>
          </div>
        </div>
      </section>
      <footer className="site-footer" aria-label="Site footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <h2 className="footer-brand-title">Arya Homoeo Hall</h2>
              <p className="footer-brand-body">
                Healing Since 1985. Over 40 years of trust, compassion, and natural wellness. Specializing in paralysis and
                polio care through proven homeopathic methods.
              </p>
              <div className="footer-badge">
                <span className="footer-badge-icon">♡</span>
                <span className="footer-badge-text">41 Years of Healing Heritage</span>
              </div>
            </div>
            <div className="footer-column">
              <h3 className="footer-heading">Quick Links</h3>
              <ul className="footer-link-list">
                <li>
                  <a href="#services" className="footer-link">
                    Our Services
                  </a>
                </li>
                <li>
                  <a href="#legacy" className="footer-link">
                    Our Legacy
                  </a>
                </li>
                <li>
                  <a href="#contact" className="footer-link">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
            <div className="footer-column">
              <h3 className="footer-heading">Specializations</h3>
              <ul className="footer-link-list">
                <li className="footer-link">Paralysis Recovery</li>
                <li className="footer-link">Polio Management</li>
                <li className="footer-link">Neural Care</li>
                <li className="footer-link">Chronic Pain Relief</li>
              </ul>
            </div>
          </div>
          <div className="footer-divider" />
          <div className="footer-meta">
            <div className="footer-meta-left">© 2024 Arya Homoeo Hall. All rights reserved. • Est. 1985</div>
              <div className="footer-meta-right">
                <SocialIcons />
              </div>
          </div>
          <div className="footer-divider" />
          <p className="footer-disclaimer">
            Disclaimer: Homeopathic treatments at Arya Homoeo Hall are complementary approaches. Individual results may
            vary. Always consult with healthcare professionals for serious medical conditions. Our treatments are based
            on 41 years of clinical experience and traditional homeopathic principles.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
